import {
  useAddFavoritesProductMutation,
  useDeleteFavoritesProductMutation,
} from '@graphql/mutations/__generated__/me.generated'
import { useCartUpdateProductVariantMutation } from '@graphql/mutations/__generated__/product-reflection.generated'
import { CartDocument, useCartLinesQuery, useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { useMeFavoritesProductsQuery, useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useSnackbar } from 'notistack'
import React, { useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { MetaWrapper } from '../../components/Meta'
import ProductDetailsDialog from '../../components/Product/ProductDetailsDialog'
import CartLineEditPanel from './components/CartLineEditPanel'
import Page from './Page'
import { CartParams } from './types'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../../apolloClient'
import { Redirect } from 'react-router-dom'
import { WHOLESALE_CART_URL } from '../../urls'
import { useRewardProgramsQuery } from '@graphql/queries/__generated__/reward-programs.generated'
import { useTranslation } from 'react-i18next'
import { DataLayerItem, googlePushProductRemoveFromCart, googlePushViewCart } from '../../utils/dataLayer'
import _ from 'lodash'
import { useCurrentCurrencyExchangeRateQuery } from '@graphql/queries/__generated__/shipping.generated'

const PAGINATE_FAVORITES = 3

const View: React.FC = ({}) => {
  const { t } = useTranslation()
  const [params, setParams] = useQueryParams(CartParams)
  const { enqueueSnackbar } = useSnackbar()
  const [lineLoading, setLineLoading] = useState<number | null>(null)
  const [productDialogData, setProductDialogData] = useState<{
    pk: string | number
    slug: string
  } | null>(null)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const meQuery = useMeQuery()
  const cartLinesQueryResult = useCartLinesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      cartType: CartTypeEnum.Default,
      limit: params.paginateBy || 10,
      offset: ((params.page || 1) - 1) * (params.paginateBy || 10),
      supplyIsNull: true,
    },
    onCompleted: data => {
      if (!data?.cartLines.results.length && params.page > 1) {
        setParams({ page: params.page - 1 }, 'replaceIn')
        return
      }
      const lines = data?.cartLines.results || []

      const items: DataLayerItem[] = lines.map((line, idx) => {
        const variant = line.variant
        const price = !line.supplyParts.length
          ? line.unitPrice.discount
          : _.sumBy(line.supplyParts, part => part.unitPrice.discount * part.quantity)
        return {
          item_id: variant.sku,
          item_name: line.variant.product.siteSpecifics[0]?.name,
          index: idx,
          item_variant: variant.properties.map(prop => `${prop.attributeName}: ${prop.valueName}`).join(),
          quantity: line.quantity,
          price,
        }
      })
      googlePushViewCart({
        currency: lines[0]?.unitPrice.currency || 'USD',
        value: _.sumBy(items, item => item.price * item.quantity),
        items,
      })
    },
    skip: isWholesaleMode,
  })
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: CartTypeEnum.Default },
  })

  const currencyExchangeRateQuery = useCurrentCurrencyExchangeRateQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  const [cartUpdateVariant] = useCartUpdateProductVariantMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Default },
      },
      'CartShippingDiscounts',
    ],
  })
  const [addToFavorites] = useAddFavoritesProductMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesCategoriesInfo'],
  })

  const favoritesQueryResult = useMeFavoritesProductsQuery({
    variables: {
      limit: PAGINATE_FAVORITES,
      offset: ((params.favoritesPage || 1) - 1) * PAGINATE_FAVORITES,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (!data?.me?.wishedProducts.results.length && params.favoritesPage > 1) {
        setParams({ favoritesPage: params.favoritesPage - 1 }, 'replaceIn')
      }
    },
    skip: isWholesaleMode,
  })
  const [removeFromFavorites] = useDeleteFavoritesProductMutation({
    awaitRefetchQueries: false,
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesCategoriesInfo'],
  })

  const rewardsQuery = useRewardProgramsQuery({
    variables: { first: 1 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const handleSignIn = () => {
    setParams({ global_dialog_form: 'signIn' }, 'replaceIn')
  }

  const onClickProduct = (id: number, slug: string) => {
    setProductDialogData({ pk: id, slug })
  }
  const handleEditQty = async (linePk: number, qty: number) => {
    setLineLoading(linePk)
    try {
      const lines = cartLinesQueryResult.data.cartLines.results
      const line = lines.find(line => line.pk === linePk)
      const variant = line.variant
      await cartUpdateVariant({
        variables: {
          variant: variant.pk,
          cartLine: linePk,
          quantity: qty,
          cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default,
        },
      })
      const price = !line.supplyParts.length
        ? line.unitPrice.discount
        : _.sumBy(line.supplyParts, part => part.unitPrice.discount * part.quantity)
      const items: DataLayerItem[] = [
        {
          item_id: variant.sku,
          item_name: line.variant.product.siteSpecifics[0]?.name,
          index: 0,
          item_variant: variant.properties.map(prop => `${prop.attributeName}: ${prop.valueName}`).join(),
          quantity: line.quantity,
          price,
        },
      ]
      googlePushProductRemoveFromCart({ currency: line.unitPrice.currency, value: price * line.quantity, items })
      enqueueSnackbar(
        t('snackbarMessages.success.shoppingBagModified', 'Your shopping bag has been modified successfully!'),
        {
          variant: 'success',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }
  const handleRemoveLine = async (linePk: number) => {
    await handleEditQty(linePk, 0)
  }
  const handleSaveForLaterLine = async (productPk: number, linePk: number) => {
    setLineLoading(linePk)
    try {
      await addToFavorites({
        variables: {
          pk: productPk,
        },
      })
      await handleRemoveLine(linePk)
      enqueueSnackbar(t('snackbarMessages.success.productAddedToFavorites', 'Product has been added to favorites.'), {
        variant: 'success',
      })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }
  const handleEditLine = async (linePk: number) => {
    setParams({ editLinePk: linePk }, 'replaceIn')
  }

  const handleRemoveFromFavorites = async (productPk: number) => {
    setLineLoading(productPk)
    try {
      await removeFromFavorites({
        variables: {
          pk: productPk,
        },
      })
      enqueueSnackbar(
        t('snackbarMessages.error.productRemovedFromFavorites', 'Product has been removed from favorites.'),
        {
          variant: 'error',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  if (isWholesaleMode && !!meQuery.data?.me) {
    return <Redirect to={WHOLESALE_CART_URL} />
  }

  return (
    <>
      <MetaWrapper titleDetails={t('metaTitle.shoppingBag', 'Shopping Bag')}>
        <Page
          handleSignIn={handleSignIn}
          cartLinesQueryResult={cartLinesQueryResult}
          cartQueryResult={cartQueryResult}
          handleEditQty={handleEditQty}
          isAuthenticated={!!meQuery.data?.me}
          handleRemoveLine={handleRemoveLine}
          handleEditLine={handleEditLine}
          handleSaveForLaterLine={handleSaveForLaterLine}
          lineLoading={lineLoading}
          onClickProduct={onClickProduct}
          favoritesQueryResult={favoritesQueryResult}
          handleRemoveFromFavorites={handleRemoveFromFavorites}
          params={params}
          setParams={setParams}
          meQueryResult={meQuery}
          firstRewardProgram={rewardsQuery?.data?.rewardPrograms.edges[0]}
          currencyExchangeRateQueryResult={currencyExchangeRateQuery}
        />
      </MetaWrapper>
      <ProductDetailsDialog spec={productDialogData} isOpen={!!productDialogData} setIsOpen={setProductDialogData} />
      <CartLineEditPanel setLoading={setLineLoading} cartType={CartTypeEnum.Default} />
    </>
  )
}

export default View
